import type { Null, RecordKey, Reflectable } from '@ricoh/ria.client.core/types/core/data.type';

import type { EnumValueDescriptor } from './object.type';

/**
 * Check type.
 *
 * @template TData Data type.
 * @param {unknown} obj Object.
 * @param {TData['$is']} $is Type name.
 *
 * @returns {boolean} Is specified type.
 */
export function isType<TData extends Reflectable<string>>(obj: unknown, $is: TData['$is']): obj is TData {
  if (typeof obj !== 'object' || !obj) {
    return false;
  }

  return '$is' in obj && obj.$is === $is;
}

/**
 * Verify Null.
 *
 * @param {unknown} obj Object.
 *
 * @returns {boolean} Is Null.
 */
export function isNull(obj: unknown): obj is Null {
  return obj === undefined || obj === null;
}

/**
 * Verify not Null.
 *
 * @template TData Data type.
 * @param {unknown} obj Object.
 *
 * @returns {boolean} Is not Null.
 */
export function isNotNull<TData>(obj: unknown): obj is NonNullable<TData> {
  return !isNull(obj);
}

/**
 * Get string value.
 *
 * @template TValue Value type.
 * @param {unknown} value Value.
 *
 * @returns {string} String value.
 */
export function getStringValue<TValue extends string>(value: unknown): TValue {
  if (typeof value === 'function') {
    return value();
  }

  if (typeof value === 'string') {
    return value as TValue;
  }

  return `${value}` as TValue;
}

/**
 * Pick object.
 *
 * @template TData Data type.
 * @template TKey Key type.
 * @param {object} obj Object.
 * @param {TKey[]} keys Picked key.
 *
 * @returns {Omit<TData, TKey>} Picked object.
 */
export function pick<TData extends object, TKey extends RecordKey>(obj: TData, ...keys: TKey[]): Omit<TData, TKey> {
  if (!obj || typeof obj !== 'object' || !keys.length) {
    return obj;
  }

  return Object.fromEntries(Object.entries(obj).filter(([key]) => keys.includes(key as TKey))) as Omit<TData, TKey>;
}

/**
 * Omit object.
 *
 * @template TData Data type.
 * @template TKey Key type.
 * @param {object} obj Object.
 * @param {TKey[]} keys Omitted key.
 *
 * @returns {Omit<TData, TKey>} Omitted object.
 */
export function omit<TData extends object, TKey extends RecordKey>(obj: TData, ...keys: TKey[]): Omit<TData, TKey> {
  if (!obj || typeof obj !== 'object' || !keys.length) {
    return obj;
  }

  return Object.fromEntries(Object.entries(obj).filter(([key]) => !keys.includes(key as TKey))) as Omit<TData, TKey>;
}

/**
 * Compare two objects.
 *
 * @template TData Data type.
 * @param {TData} obj1 Object 1.
 * @param {TData} obj2 Object 2.
 *
 * @returns {number} Compare value:\
 *   If `obj1` > `obj2`, value = `1`.\
 *   If `obj1` < `obj2`, value = `-1`.\
 *   If `obj1` == `obj2`, value = `0`.
 */
export function compare<TData>(obj1: TData, obj2: TData): number {
  if (isNull(obj1)) {
    return obj1 === obj2 ? 0 : -1;
  }

  if (isNull(obj2)) {
    return 1;
  }

  if (typeof obj1 === 'string' && typeof obj2 === 'string') {
    return obj1.localeCompare(obj2);
  }

  if (obj1 > obj2) {
    return 1;
  }

  if (obj1 < obj2) {
    return -1;
  }

  return 0;
}

/**
 * Sort array.
 *
 * @template TData Data type.
 * @param {TData[]} arr Array to sort.
 * @param {'asc' | 'desc'} direction Direction.
 *
 * @returns {TData[]} Sorted array.
 */
export function order<TData>(arr: TData[], direction: 'asc' | 'desc' = 'asc'): TData[] {
  if (direction === 'asc') {
    return arr.toSorted(compare);
  }

  return arr.toSorted((item1, item2) => compare(item2, item1));
}

/**
 * Normalize object.
 *
 * @template TData Data type.
 * @param {TData} obj Object.
 *
 * @returns {TData} Normalized object.
 */
export function normalizeObject<TData>(obj: TData): TData {
  if (isNull(obj)) {
    return obj;
  }

  return JSON.parse(JSON.stringify(obj));
}

/**
 * Get enum value descriptor.
 *
 * @template TEnum Enum type.
 * @template TName Enum key type.
 * @param {TEnum} enumObj Enum object.
 * @param {TName} key Enum key.
 *
 * @returns {EnumValueDescriptor<TEnum, TName>} Enum value descriptor.
 */
export function enumValue<TEnum extends object, TName extends keyof TEnum>(enumObj: TEnum, key: TName): EnumValueDescriptor<TEnum, TName> {
  return {
    name: key,
    value: enumObj[key],
  } as unknown as EnumValueDescriptor<TEnum, TName>;
}

/**
 * Inject enum.
 *
 * @template TEnum Enum type.
 * @template TName Enum key type.
 * @param {TEnum} enumObj Enum object.
 * @param {TName} key Enum key.
 * @param {unknown} value Value.
 */
export function injectEnum<TEnum extends object, TName extends string>(enumObj: TEnum, key: TName, value: unknown) {
  enumObj[value as keyof TEnum] = key as TEnum[keyof TEnum];
  enumObj[key as unknown as keyof TEnum] = value as TEnum[keyof TEnum];
}

/**
 * Get value of path.
 *
 * @template TData Data type.
 * @template TValue Value type.
 * @param {TData} obj Object.
 * @param {string} path Key path.
 *
 * @returns {TValue} Value.
 */
export function getValueByPath<TData extends object, TValue>(obj: TData, path: string): TValue | undefined {
  return path
    .toString()
    .split('.')
    .reduce<TValue | undefined>(
      (prev, key) => {
        if (isNotNull(prev)) {
          return prev[key as keyof TValue] as TValue;
        }

        return undefined;
      },
      obj as unknown as TValue,
    );
}

/**
 * Get boolean value.
 *
 * @param {unknown} value Value.
 *
 * @returns {boolean} Boolean value.
 */
export function boolean(value: unknown): boolean {
  const preparedValue = typeof value === 'function' ? value() : value;

  switch (typeof preparedValue) {
    case 'string': {
      return ['true', 't', 'yes', 'y', 'on', '1'].includes(preparedValue.trim().toLowerCase());
    }

    case 'number': {
      return preparedValue === 1;
    }

    case 'boolean': {
      return preparedValue;
    }

    default: {
      return false;
    }
  }
}

/**
 * Swap object properties.
 *
 * @template TData Data type.
 * @param {TData} obj Object.
 * @param {keyof TData} prop1 Property 1.
 * @param {keyof TData} prop2 Property 2.
 */
export function swapProps<TData extends object>(obj: TData, prop1: keyof TData, prop2: keyof TData) {
  [obj[prop1], obj[prop2]] = [obj[prop2], obj[prop1]];
}
